import { Box, HStack, Progress, Spinner, Text, VStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import React from "react";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverDocumentUploadId, CaregiverId } from "../../../../../shared/schema/schema";
import EntityProfileActionBar from "../../../../entityProfileModal/components/EntityProfileActionBar";
import EntityModalButton, {
  EntityModalButtonGroup,
} from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable, {
  EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../entityProfileModal/entityProfileModal.theme";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

type ApplicationStage = {
  id: number;
  label: string;
  name?: string;
  labelColor?: string;
  order?: number;
};

type ApplicationStagesResponse = {
  sectionStages?: Record<string, ApplicationStage[]>;
  stages?: ApplicationStage[];
};

type ApplicationQuestion = {
  answer?: unknown;
  answerRejectionStatus?: { status?: string };
  chosenAnswerId?: unknown;
  chosenAnswerIds?: unknown[];
  complianceRejectionData?: { type?: string };
  isHidden?: boolean;
  isMandatory?: boolean;
  type?: string;
};

type ApplicationQuestionGroup = {
  questions?: ApplicationQuestion[];
};

type ApplicationSubsection = {
  data: {
    id: number;
    title: string;
  };
  document?: {
    isDocumentGenerated?: boolean;
  } | null;
  questionGroups?: ApplicationQuestionGroup[];
};

type ApplicationSection = {
  data?: {
    HRSectionStageId?: number | null;
    id: number;
    isStop?: boolean;
    title: string;
  };
  id?: number;
  progressPercentage: number;
  stageId?: number | null;
  subsectionCount: number;
  subsectionsDoneButNotGenerated: number;
  title?: string;
  subSections: ApplicationSubsection[];
};

type ApplicationAnswersResponse = {
  caregiverSectionStageId?: number | null;
  caregiverSectionStageLabel?: string | null;
  sections: ApplicationSection[];
};

type CaregiverDocumentUpload = {
  createdAt?: string;
  documentType?: number | null;
  fileName?: string | null;
  id: CaregiverDocumentUploadId;
  name?: string | null;
  subsection?: number | null;
};

type CaregiverDocumentsResponse = {
  documents: CaregiverDocumentUpload[];
};

type ApplicationSortKey = "name" | "progress" | "missing";

function isQuestionAnswered(question: ApplicationQuestion) {
  return (
    (question.answer !== null && question.answer !== undefined) ||
    (question.chosenAnswerId !== null && question.chosenAnswerId !== undefined) ||
    (question.chosenAnswerIds?.length ?? 0) > 0
  );
}

function isQuestionRejected(question: ApplicationQuestion) {
  return (
    (question.type !== "Document" && question.answerRejectionStatus?.status === "Rejected") ||
    (question.type === "Document" && question.complianceRejectionData?.type === "Rejected")
  );
}

function deriveSections(rawSections: ApplicationSection[]) {
  let hasAnyCompletedButNotGeneratedSubsection = false;
  let hasAnyAnsweredQuestion = false;

  const sections = rawSections.map((rawSection) => {
    const section = normalizeApplicationSection(rawSection);
    let finishedSubsections = 0;
    let subsectionCount = 0;
    let subsectionsDoneButNotGenerated = 0;
    const subSections = section.subSections ?? [];

    subSections.forEach((subsection) => {
      const questionGroups = subsection.questionGroups ?? [];

      if (questionGroups.length === 0) {
        finishedSubsections += 1;
        return;
      }

      let completedQuestionGroups = 0;

      questionGroups.forEach((questionGroup) => {
        const questions = questionGroup.questions ?? [];

        if (questions.some(isQuestionAnswered)) {
          hasAnyAnsweredQuestion = true;
        }

        const completedQuestions = questions.filter(
          (question) =>
            !question.isMandatory ||
            ((isQuestionAnswered(question) || question.isHidden === true) &&
              !isQuestionRejected(question))
        );

        if (completedQuestions.length === questions.length) {
          completedQuestionGroups += 1;
        }
      });

      if (completedQuestionGroups === questionGroups.length) {
        if (subsection.document?.isDocumentGenerated === false) {
          subsectionsDoneButNotGenerated += 1;
          hasAnyCompletedButNotGeneratedSubsection = true;
        } else {
          finishedSubsections += 1;
        }

        subsectionCount += 1;
      }
    });

    return {
      ...section,
      progressPercentage:
        subSections.length === 0 ? 0 : Math.round((finishedSubsections / subSections.length) * 100),
      subsectionCount,
      subsectionsDoneButNotGenerated,
    };
  });

  return { hasAnyAnsweredQuestion, hasAnyCompletedButNotGeneratedSubsection, sections };
}

function normalizeApplicationSection(section: ApplicationSection): Required<ApplicationSection> {
  return {
    ...section,
    data: section.data ?? {
      HRSectionStageId: section.stageId ?? null,
      id: section.id ?? 0,
      title: section.title ?? "",
    },
    id: section.id ?? section.data?.id ?? 0,
    progressPercentage: section.progressPercentage ?? 0,
    stageId: section.stageId ?? section.data?.HRSectionStageId ?? null,
    subsectionCount: section.subsectionCount ?? 0,
    subsectionsDoneButNotGenerated: section.subsectionsDoneButNotGenerated ?? 0,
    title: section.title ?? section.data?.title ?? "",
    subSections: section.subSections ?? [],
  };
}

function getCertificationStages(
  response: ApplicationStagesResponse | undefined,
  selectedCertification: string | null
) {
  if (selectedCertification === null) {
    return [];
  }

  return (
    response?.sectionStages?.[selectedCertification] ??
    response?.stages?.map((stage) => ({
      ...stage,
      label: stage.label ?? stage.name ?? "",
    })) ??
    []
  );
}

function getDownloadFileName(file: CaregiverDocumentUpload) {
  return file.fileName ?? file.name ?? `caregiver-document-${file.id}`;
}

export default function CaregiverProfileApplicationSection(props: Props) {
  const { api } = useApi();
  const toast = useEntityProfileToast();
  const queryClient = useQueryClient();
  const caregiverId = props.caregiverId;
  const isOnboardingAgency = auth.getAuthOrFail().isMedflytAgencyMember;
  const [selectedCertification, setSelectedCertification] = React.useState<string | null>(null);
  const [zipUrl, setZipUrl] = React.useState<string | null>(null);

  const certificationsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiverId, "applicationCertifications"],
    queryFn: () =>
      api.get(
        "/hr/agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/application_certifications",
        { path: { caregiverId } }
      ),
  });

  const certifications = React.useMemo(
    () => certificationsQuery.data?.certifications ?? [],
    [certificationsQuery.data?.certifications]
  );

  React.useEffect(() => {
    if (selectedCertification === null && certifications.length > 0) {
      setSelectedCertification(certifications[0]);
    }
  }, [certifications, selectedCertification]);

  const stagesQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiverId, "applicationStages"],
    queryFn: () =>
      api.anyRequest<ApplicationStagesResponse>(
        "GET",
        "/hr/agencies/:agencyId/agency_members/:agencyMemberId/hr_section_stages",
        {}
      ),
  });

  const answersQuery = useQuery({
    enabled: selectedCertification !== null,
    queryKey: ["caregiverProfileModal", caregiverId, "applicationAnswers", selectedCertification],
    queryFn: () =>
      api.anyRequest<ApplicationAnswersResponse>(
        "GET",
        "/hr/agencies/:agencyId/caregivers/:caregiverId/certifications/:certification/caregiver_application_answers",
        { path: { caregiverId, certification: selectedCertification } }
      ),
  });

  const documentsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiverId, "applicationDocuments"],
    queryFn: () =>
      api.anyRequest<CaregiverDocumentsResponse>(
        "GET",
        "/hr/agencies/:agencyId/caregivers/:caregiverId/documents",
        { path: { caregiverId } }
      ),
  });

  const copyApplicationMutation = useMutation({
    mutationFn: (fromCertification: string) =>
      api.post(
        "/hr/agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/copy_application",
        {
          path: { caregiverId },
          body: {
            fromCertification,
            toCertification: fromCertification === "HHA" ? "PCA" : "HHA",
          },
        }
      ),
    onSuccess: () => {
      toast({ status: "success", title: "Application copied" });
      queryClient.invalidateQueries({
        queryKey: ["caregiverProfileModal", caregiverId, "applicationAnswers"],
      });
    },
    onError: () => {
      toast({ status: "error", title: "Failed to copy application" });
    },
  });

  const generateZipMutation = useMutation({
    mutationFn: async () => {
      const files = documentsQuery.data?.documents ?? [];

      if (files.length === 0) {
        throw new Error("NO_FILES");
      }

      const zip = new JSZip();
      const filesWithUrls = await Promise.all(
        files.map(async (file) => {
          const result = await api.get(
            "/agencies/:agencyId/caregivers/:caregiverId/document_uploads/:documentUploadId/generate_url",
            { path: { caregiverId, documentUploadId: file.id } }
          );

          return { file, url: result.fileUrl };
        })
      );

      const fileBlobs = await Promise.all(
        filesWithUrls.map(async ({ file, url }) => {
          const response = await fetch(url);

          return { blob: await response.blob(), file };
        })
      );

      fileBlobs.forEach(({ blob, file }) => {
        zip.file(getDownloadFileName(file), blob);
      });

      return zip.generateAsync({ type: "blob" });
    },
    onSuccess: (blob) => {
      if (zipUrl !== null) {
        URL.revokeObjectURL(zipUrl);
      }

      setZipUrl(URL.createObjectURL(blob));
    },
    onError: (error) => {
      toast({
        status: "warning",
        title:
          error instanceof Error && error.message === "NO_FILES"
            ? "No files"
            : "Could not generate zip",
      });
    },
  });

  React.useEffect(
    () => () => {
      if (zipUrl !== null) {
        URL.revokeObjectURL(zipUrl);
      }
    },
    [zipUrl]
  );

  const derivedApplication = React.useMemo(
    () => deriveSections(answersQuery.data?.sections ?? []),
    [answersQuery.data?.sections]
  );
  const currentStages = getCertificationStages(stagesQuery.data, selectedCertification);
  const currentStage = currentStages.find(
    (stage) => stage.id === answersQuery.data?.caregiverSectionStageId
  );
  const showCompletedButNotGenerated = derivedApplication.hasAnyCompletedButNotGeneratedSubsection;
  const isLoading =
    certificationsQuery.isLoading ||
    stagesQuery.isLoading ||
    answersQuery.isLoading ||
    documentsQuery.isLoading;
  const isRefreshing =
    certificationsQuery.isFetching ||
    stagesQuery.isFetching ||
    answersQuery.isFetching ||
    documentsQuery.isFetching;

  const columns = React.useMemo<
    Array<EntityProfileDataTableColumn<ApplicationSection, ApplicationSortKey>>
  >(
    () => [
      {
        key: "name",
        header: "Name",
        sortKey: "name",
        sortValue: (section) => section.data?.title ?? section.title ?? "",
        render: (section) => section.data?.title ?? section.title ?? "",
        cellProps: { fontWeight: 500 },
      },
      {
        key: "progress",
        header: "Progress",
        sortKey: "progress",
        sortValue: (section) => section.progressPercentage,
        render: (section) => (
          <Box maxW="360px" minW="220px">
            <Progress
              borderRadius="full"
              colorScheme="blue"
              h="14px"
              value={section.progressPercentage}
            />
            <Text color={entityProfileModalColors.secondaryText} fontSize="12px" mt={1}>
              {section.progressPercentage}%
            </Text>
          </Box>
        ),
      },
      ...(showCompletedButNotGenerated
        ? [
            {
              key: "missing",
              header: "Completed But Not Generated Subsections",
              sortKey: "missing" as const,
              sortValue: (section: ApplicationSection) => section.subsectionsDoneButNotGenerated,
              render: (section: ApplicationSection) =>
                section.subsectionsDoneButNotGenerated > 0 ? (
                  <Box
                    as="span"
                    bg="#f8ac59"
                    borderRadius="3px"
                    color="white"
                    display="inline-block"
                    fontWeight={700}
                    px={2}
                    py={1}
                  >
                    {section.subsectionsDoneButNotGenerated}
                  </Box>
                ) : null,
              cellProps: { textAlign: "center" as const },
            },
          ]
        : []),
    ],
    [showCompletedButNotGenerated]
  );

  const openLegacyApplication = () => {
    if (props.caregiver === undefined) {
      return;
    }

    props.actions.openLegacyCaregiverSection({
      caregiver: props.caregiver,
      legacySectionLabel: "Compliance",
      legacySubsectionLabel: "Application",
    });
  };

  return (
    <VStack align="stretch" spacing={4}>
      {isRefreshing && !isLoading ? <Progress isIndeterminate colorScheme="blue" h="2px" /> : null}
      <HStack align="flex-start" justify="space-between" spacing={4}>
        <VStack align="flex-start" spacing={3}>
          {certifications.length > 0 ? (
            <EntityModalButtonGroup>
              {certifications.map((certification) => (
                <EntityModalButton
                  key={certification}
                  kind="blue"
                  size="sm"
                  variant={selectedCertification === certification ? "solid" : "outline"}
                  onClick={() => {
                    setZipUrl(null);
                    setSelectedCertification(certification);
                  }}
                >
                  {certification}
                </EntityModalButton>
              ))}
            </EntityModalButtonGroup>
          ) : null}
          {currentStage !== undefined || answersQuery.data?.caregiverSectionStageLabel ? (
            <HStack spacing={2}>
              <Text color={entityProfileModalColors.secondaryText} fontWeight={700}>
                Stage:
              </Text>
              <Box
                bg={currentStage?.labelColor ?? entityProfileModalColors.activeMuted}
                borderRadius="3px"
                color="white"
                fontSize="13px"
                fontWeight={700}
                px={2}
                py={1}
              >
                {currentStage?.label ?? answersQuery.data?.caregiverSectionStageLabel}
              </Box>
            </HStack>
          ) : null}
        </VStack>
        <EntityProfileActionBar>
          {isOnboardingAgency && certifications.length > 1 && selectedCertification !== null ? (
            <EntityModalButton
              isLoading={copyApplicationMutation.isPending}
              kind="white"
              size="sm"
              onClick={() => {
                if (window.confirm(`Copy ${selectedCertification} application?`)) {
                  copyApplicationMutation.mutate(selectedCertification);
                }
              }}
            >
              Copy {selectedCertification} application
            </EntityModalButton>
          ) : null}
          {zipUrl === null ? (
            <EntityModalButton
              isLoading={generateZipMutation.isPending}
              kind="blue"
              size="sm"
              onClick={() => generateZipMutation.mutate()}
            >
              Generate Caregiver&apos;s Zip File
            </EntityModalButton>
          ) : (
            // Rendered as an anchor rather than EntityModalButton so the
            // browser handles the `download` attribute; the bootstrap classes keep
            // it looking identical to the surrounding buttons.
            <a
              className="btn btn-sm btn-primary"
              download={`${props.caregiver?.firstName ?? "caregiver"}-${
                props.caregiver?.lastName ?? "application"
              }.zip`}
              href={zipUrl}
              onClick={() => setZipUrl(null)}
            >
              Download {props.caregiver?.firstName ?? "caregiver"}{" "}
              {props.caregiver?.lastName ?? "application"} file
            </a>
          )}
          {derivedApplication.hasAnyAnsweredQuestion && props.caregiver !== undefined ? (
            <EntityModalButton kind="blue" size="sm" onClick={openLegacyApplication}>
              Create applicant profile (PDF)
            </EntityModalButton>
          ) : null}
        </EntityProfileActionBar>
      </HStack>

      <Box>
        <Text color={entityProfileModalColors.title} fontSize="18px" fontWeight={500} mb={3}>
          Sections
        </Text>
        {isLoading ? (
          <HStack
            border={entityProfileModalBorders.default}
            borderRadius="2px"
            color={entityProfileModalColors.secondaryText}
            justify="center"
            minH="120px"
          >
            <Spinner size="sm" />
            <Text>Loading application</Text>
          </HStack>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            getRowKey={(section) => section.data.id}
            minW="760px"
            rowProps={(section) => ({
              cursor: props.caregiver === undefined ? "default" : "pointer",
              onClick: openLegacyApplication,
              title:
                props.caregiver === undefined
                  ? undefined
                  : `Open ${section.data.title} in application details`,
            })}
            rows={derivedApplication.sections.filter(
              (section) => !section.data.isStop && section.data.title !== "Documents"
            )}
            showEmptyState={false}
            tableSx={{
              "td, th": {
                fontSize: "14px",
              },
            }}
          />
        )}
      </Box>
    </VStack>
  );
}
